import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { getTopPerformingGames, getStudioPerformance } from '../../../utils/dashboardService';

const TopPerformers = () => {
  const [activeTab, setActiveTab] = useState('games');
  const [games, setGames] = useState([]);
  const [studios, setStudios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [gamesData, studiosData] = await Promise.all([
          getTopPerformingGames(5),
          getStudioPerformance(5)
        ]);
        setGames(gamesData || []);
        setStudios(studiosData || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching top performers:', err);
        setError('Failed to load performance data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <Icon key={i} name="Star" size={12} className="text-yellow-400 fill-current" />
      );
    }

    if (hasHalfStar) {
      stars.push(
        <Icon key="half" name="Star" size={12} className="text-yellow-400 fill-current opacity-50" />
      );
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <Icon key={`empty-${i}`} name="Star" size={12} className="text-muted-foreground" />
      );
    }

    return stars;
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-foreground">Top Performers</h3>
        </div>
        <div className="space-y-4">
          {[...Array(5)].map((_, index) => (
            <div key={index} className="flex items-center space-x-4 animate-pulse">
              <div className="w-12 h-12 bg-muted rounded-lg"></div>
              <div className="flex-1">
                <div className="w-32 h-4 bg-muted rounded mb-2"></div>
                <div className="w-24 h-3 bg-muted rounded"></div>
              </div>
              <div className="w-16 h-4 bg-muted rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-foreground">Top Performers</h3>
        </div>
        <div className="text-center py-8">
          <Icon name="AlertTriangle" size={24} className="text-destructive mx-auto mb-2" />
          <p className="text-destructive font-medium">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Top Performers</h3>
          <p className="text-sm text-muted-foreground">Best performing content and partners</p>
        </div>
        
        <div className="flex items-center bg-muted/30 rounded-lg p-1">
          <Button
            variant={activeTab === 'games' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('games')}
          >
            Games
          </Button>
          <Button
            variant={activeTab === 'studios' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('studios')}
          >
            Studios
          </Button>
        </div>
      </div>

      {activeTab === 'games' ? (
        <div className="space-y-4">
          {games.length === 0 ? (
            <div className="text-center py-8">
              <Icon name="Gamepad2" size={48} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No games data available</p>
            </div>
          ) : (
            games.map((game, index) => (
              <div key={game.id} className="flex items-center space-x-4 p-3 rounded-lg hover:bg-muted/30 transition-colors">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-bold text-sm">
                  {index + 1}
                </div>
                
                <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                  {game.coverImage && game.coverImage !== '/assets/images/no_image.png' ? (
                    <img 
                      src={game.coverImage} 
                      alt={game.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Icon name="Gamepad2" size={20} className="text-muted-foreground" />
                    </div>
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-foreground truncate">{game.title}</h4>
                  <p className="text-sm text-muted-foreground truncate">{game.studio}</p>
                  <div className="flex items-center space-x-1 mt-1">
                    {renderStars(game.rating)}
                    <span className="text-xs text-muted-foreground ml-2">
                      {game.rating.toFixed(1)}
                    </span>
                  </div>
                </div>
                
                <div className="text-right flex-shrink-0">
                  <div className="text-sm font-medium text-foreground">
                    {formatNumber(game.playCount)}
                  </div>
                  <div className="text-xs text-muted-foreground">plays</div>
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {studios.length === 0 ? (
            <div className="text-center py-8">
              <Icon name="Building2" size={48} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No studios data available</p>
            </div>
          ) : (
            studios.map((studio, index) => (
              <div key={studio.id} className="flex items-center space-x-4 p-3 rounded-lg hover:bg-muted/30 transition-colors">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-secondary/10 text-secondary font-bold text-sm">
                  {index + 1}
                </div>
                
                <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                  {studio.logoUrl ? (
                    <img 
                      src={studio.logoUrl} 
                      alt={studio.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Icon name="Building2" size={20} className="text-muted-foreground" />
                    </div>
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-foreground truncate">{studio.name}</h4>
                  <p className="text-sm text-muted-foreground">
                    {studio.gameCount} game{studio.gameCount !== 1 ? 's' : ''} • {formatNumber(studio.totalPlays)} plays
                  </p>
                  <div className="flex items-center space-x-1 mt-1">
                    {renderStars(studio.avgRating)}
                    <span className="text-xs text-muted-foreground ml-2">
                      {studio.avgRating.toFixed(1)} avg
                    </span>
                  </div>
                </div>
                
                <div className="text-right flex-shrink-0">
                  <div className="text-sm font-medium text-foreground">
                    {formatCurrency(studio.totalRevenue)}
                  </div>
                  <div className="text-xs text-muted-foreground">revenue</div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default TopPerformers;
